import React from "react";
import Profile from "../images/profile.jpg";
import Example from "./Example";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";

const ExperienceConfig = [
  {
    title: "Software Engineer, Deutsche bank",
    period: "10, July 2023 - Present",
    location: "Pune, Mahrashtra",
    skills: ["React", "Redux", "Typescript", "CI/CD", "Docker", "Kubernetes", "Springboot", "Testing"],
    description: [
      "Migrated an active, large-scale production codebase from JavaScript to TypeScript in phased, feature-wise iterations, improving type safety and reducing runtime errors by 40%.",
      "Designed & deployed a full-stack support app with dashboards for role management, audit history, and Kafka listener controls, improving microservice visibility and reducing team’s response time by 25%.",
      "Collaborated with teammates to deliver 10+ features for a large-scale production app used by 50+ stakeholders, ensuring alignment with business requirements for on-time, accurate delivery.",
      "Redesigned Dockerfiles (20→4 lines) and shifted dependency installs to GitHub Actions, cutting frontend deployment time by 60% and improving build maintainability by bundling the server via Webpack.",
      "Migrated build tool from Webpack to Vite, boosting build speed and local startup by 3×, and automated the build pipeline in GitHub Actions for faster, consistent deployments.",
      "Implemented unit tests using Jest and Vitest to achieve 70%+ coverage, and introduced Cypress E2E testing with an automated reporting flow, significantly reducing manual QA effort.",
      "Built a reverse proxy using the existing Apache HTTPD image to route traffic for all tribe applications, and configured GSLB for primary–DR routing, ensuring high availability and resilience.",
      "Developed core business logic for 5+ Spring Boot services, enabling critical workflows for 100+ stakeholders with improved reliability and maintainability.",
      "Introduced libraries including React Query, React Router, Redux Toolkit, and MSW, and bootstrapped new projects with them, accelerating development speed and improving state management efficiency."
    ],
  },
  {
    title: "Intern, Frontend engineer, Myshubhlife (DATASIGNS TECHNOLOGY)",
    period: "Jan 2023 - June 2023",
    location: "Remote",
    skills: ["React", "Typescript", "Gatsby"],
    description: [
      "Revamped internal dashboards with React+TS, enhancing UX and reducing load times.",
      "Built static landing page using Gatsby for marketing, improving SEO and initial load speed.",
      "Worked closely with design and product to streamline UI workflows",
    ],
  },
  {
    title: "Intern, SWE, JP Morgan chase & Co.",
    period: "June 2023 - July 2023",
    location: "Mumbai, Maharashtra",
    skills: ["Flask", "HTML", "JS", "CSS"],
    description: [
      "Built Flask APIs for business data, automated docs, and integrated them into internal chatbots.",
    ],
  },
];

const AnimateExperience = ({exp, delay}) => {
  const ref = useRef(null);
   const inView = useInView(ref, { triggerOnce: true, threshold: 0.1 });
  return (
    <motion.li
      ref={ref}
      initial={{ opacity: 0, x: 40 }}
      animate={inView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.4, ease: "easeIn" }}
      className="mb-10 ms-4"
    >
      <div className="absolute w-3 h-3 bg-gray-200 rounded-full mt-1.5 -start-1.5 border border-white dark:border-gray-900 dark:bg-gray-700"></div>
      <div className="flex lg:flex-row flex-col justify-between items-center p-4">
        <div className="p-2">
          <time className="mb-1 text-sm font-normal leading-none text-gray-400 dark:text-gray-500">
            {exp.period}
          </time>
          <h3 className="flex-grow lg:text-lg text-sm font-semibold text-gray-900 dark:text-white">
            {exp.title}
          </h3>
          <h6 className="mb-4 text-sm font-normal leading-none text-gray-400 dark:text-gray-500">
            {exp.location}
          </h6>
        </div>
        <div className="lg:w-1/3 flex flex-wrap justify-end gap-1">
          {exp.skills.map((skill) => (
            <span className="badge">{skill}</span>
          ))}
        </div>
      </div>

      <ol className="list-disc ms-6">
        {exp.description.map((desc) => (
          <li className="mb-2 lg:text-base text-sm font-normal text-gray-500 dark:text-gray-400">
            {desc}
          </li>
        ))}
      </ol>
      {/* <a
                  href="#"
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-900 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-4 focus:outline-none focus:ring-gray-100 focus:text-blue-700 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600 dark:hover:text-white dark:hover:bg-gray-700 dark:focus:ring-gray-700"
                >
                  Learn more{" "}
                  <svg
                    className="w-3 h-3 ms-2 rtl:rotate-180"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 14 10"
                  >
                    <path
                      stroke="currentColor"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M1 5h12m0 0L9 1m4 4L9 9"
                    />
                  </svg>
                </a> */}
    </motion.li>
  );
}

const Experience = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false });

  const ref1 = useRef(null); 
  const containerInView = useInView(ref1, {once: false});

  return (
    <motion.div 
    ref={ref1}
    initial={{opacity: 0, scale: 1.1}}
    animate={{opacity: 1, scale: 1}}
    transition={{duration: 1, ease:'easeOut'}}
    className="p-8 space-y-4">
      <motion.div
        ref={ref}
        initial={{ opacity: 0, y: 50 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}} // only animate when in view
        transition={{ duration: 0.3, ease: "easeOut" }}
        className="stats bg-transparent w-full pt-5 mb-5"
      >
        <div className="stat shadow flex items-center justify-center">
          <div className="stat-value lg:text-5xl text-md">Experience</div>
        </div>
      </motion.div>

      <section className="experience px-4 flex justify-center">
        <div className="lg:w-3/4 w-full">
          <ol className="relative border-s border-gray-200 dark:border-gray-700">
            {ExperienceConfig.map((exp, idx) => (
              <AnimateExperience exp={exp} delay={idx*0.2}/>
            ))}
          </ol>
        </div>
      </section>
    </motion.div>
  );
};

export default Experience;
